import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    Box, Button, Card, CardHeader, CardContent, Typography,
    OutlinedInput, FormControl, FormGroup, FormLabel,
    Select, MenuItem, Grid, Dialog, DialogContent
} from '@material-ui/core'
import ProviderAPI from 'config/api/ProviderAPI'
import { onError } from 'config/lib/errorLib'
import CountySelect from 'components/general/CountySelect'
import ProjectType from 'components/provider/ProjectType'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import { NotificationManager } from 'react-notifications'
import { v4 as uuidv4 } from 'uuid'
import ReactDropzoneCropper from 'components/general/ReactDropzoneCropper'
import { Storage } from 'aws-amplify'
import Loader from 'components/general/Loader'
import config from 'config/api/Config'
import CreditCardIcon from '@material-ui/icons/CreditCard'
import { getVideoEmbedUrl } from 'config/helpers/Helpers'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { cleanQuillHtml } from 'config/helpers/Helpers'

const useStyles = makeStyles(componentStyles)

function stripHtml(html) {
    html = html || '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
}

function AddProjectCard(props) {
    const classes = { ...useStyles() }
    const api = new ProviderAPI()
    const companyId = props.companyId
    const draft = props.newProjectDraft || {}
    const type = draft.type || ''

    const [image, setImage] = useState(null)
    const [imagePreview, setImagePreview] = useState(null)
    const [showVideoUrl, setShowVideoUrl] = useState(false)
    const [loading, setLoading] = useState(false)

    // Structured fields state
    const [structured, setStructured] = useState({
        panels: '',
        costAfterGrant: '',
        annualSavings: '',
    });

    // Helper: auto-calculate payback period when cost and savings are provided.
    const paybackPeriod = (structured.costAfterGrant && structured.annualSavings && Number(structured.annualSavings) > 0)
        ? (Number(structured.costAfterGrant) / Number(structured.annualSavings)).toFixed(1)
        : '';

    function updateField(path, value) {
        props.updateField(`newProjectDraft.${path}`, value)
        if (path === 'type') {
            props.updateField('newProjectDraft.videoUrl', '')
            setImage(null)
            setImagePreview(null)
            setShowVideoUrl(false)
        }
    }

    async function addProject() {
        setLoading(true)
        const projectId = uuidv4()
        try {
            let projectPath = `projects/${projectId}`

            if (image) {
                await Storage.put(projectPath, image, {
                    contentType: image.type,
                    level: 'public'
                })
            }

            const imageUrl = `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/${projectPath}`

            const details = {
                projectId,
                type,
                category: draft.category,
                county: draft.county,
                description: cleanQuillHtml(draft.description),
                image: (
                    draft.videoUrl &&
                    (draft.videoUrl.includes('youtube.com') || draft.videoUrl.includes('youtu.be') || draft.videoUrl.includes('vimeo.com'))
                ) ? draft.videoUrl : (image ? imageUrl : ''),
                // Structured fields (each optional and independent)
                panels: structured.panels || undefined,
                costAfterGrant: structured.costAfterGrant || undefined,
                annualSavings: structured.annualSavings || undefined,
                paybackPeriod: paybackPeriod || undefined
            }

            const company = await api.addProject(companyId, details)
            props.updateCompany(company)
            NotificationManager.success('Case Study added')

            // Clear UI state
            setImage(null)
            setImagePreview(null)
            setShowVideoUrl(false)
            setStructured({ panels: '', costAfterGrant: '', annualSavings: '' })

            // Clear newProjectDraft
            props.updateField('newProjectDraft', {})

            // Close dialog after adding
            props.onClose && props.onClose()
        } catch (e) {
            onError(e)
        } finally {
            setLoading(false)
        }
    }

    async function addImage(file) {
        const preview = URL.createObjectURL(file)
        setImage(file)
        setImagePreview(preview)
        updateField('videoUrl', '')
        setShowVideoUrl(false)
    }

    function checkFields() {
        // Only the Residential/Commercial category is required; everything else is optional.
        return !draft.category
    }

    function openVideoUrl() {
        setShowVideoUrl(true)
        setImage(null)
        setImagePreview(null)
    }

    const quillModules = {
        toolbar: [
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['bold', 'italic'],
        ]
    }
    const quillFormats = [
        'list', 'bullet', 'bold', 'italic'
    ]

    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="md" fullWidth>
            <DialogContent>
                <Grid container>
                    <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                        <Box component={Typography} variant='h3'>
                            <Box component='span'>Add Case Study</Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>Case Study Type</FormLabel>
                            <Grid container>
                                <Grid item xs={6}>
                                    <ProjectType
                                        title='Add Image Case Study'
                                        icon={<CreditCardIcon style={{ height: 30, width: 30, color: '#229626' }} />}
                                        selectedMethod={type}
                                        onClick={() => updateField('type', 'Image')}
                                        active={type === 'Image'}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <ProjectType
                                        title='Add Video Case Study'
                                        icon={<CreditCardIcon style={{ height: 30, width: 30, color: '#229626' }} />}
                                        selectedMethod={type}
                                        onClick={() => updateField('type', 'Video')}
                                        active={type === 'Video'}
                                    />
                                </Grid>
                            </Grid>
                        </FormGroup>
                    </Grid>

                    {type && (
                        <>
                            <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                                <FormGroup>
                                    {/* <FormLabel>Add {type === 'Image' ? 'Image' : 'Video'} of Case Study</FormLabel> */}

                                    {showVideoUrl && (
                                        <FormControl variant='filled' component={Box} width='100%' style={{ marginBottom: '1rem' }}>
                                            <OutlinedInput
                                                autoComplete='off'
                                                type='text'
                                                placeholder='www.youtube.com/my-video'
                                                value={draft.videoUrl || ''}
                                                onChange={(e) => updateField('videoUrl', e.target.value)}
                                            />
                                        </FormControl>
                                    )}

                                    <FormControl
                                        variant='filled'
                                        component={Box}
                                        width='100%'
                                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                    >
                                        {(type === 'Image' && imagePreview) && (
                                            <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' width='100%'>
                                                <img
                                                    className='case-study-preview'
                                                    src={imagePreview}
                                                    alt='Case Study Image'
                                                    style={{ marginBottom: '1rem', borderRadius: '4px', textAlign: 'center' }}
                                                />
                                            </Box>
                                        )}

                                        {(type === 'Video' && (draft.videoUrl || imagePreview)) && (
                                            <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' width='100%'>
                                                {(draft.videoUrl && (
                                                    draft.videoUrl.includes('youtube.com') ||
                                                    draft.videoUrl.includes('youtu.be') ||
                                                    draft.videoUrl.includes('vimeo.com')
                                                )) && (
                                                    <iframe
                                                        width='80%'
                                                        height='360'
                                                        src={getVideoEmbedUrl(draft.videoUrl)}
                                                        frameBorder='0'
                                                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                                                        allowFullScreen
                                                        title='Case Study Video'
                                                        style={{ marginBottom: '1rem', borderRadius: '4px' }}
                                                    />
                                                )}

                                                {imagePreview && (
                                                    <video
                                                        className='case-study-preview'
                                                        controls
                                                        style={{ marginBottom: '1rem', borderRadius: '4px', textAlign: 'center' }}
                                                    >
                                                        <source src={draft.videoUrl || imagePreview} type='video/mp4' />
                                                        Your browser does not support the video tag.
                                                    </video>
                                                )}
                                            </Box>
                                        )}
                                        
                                        <ReactDropzoneCropper
                                            updateImage={addImage}
                                            companyId={companyId}
                                            noPreview
                                            uploadText={`Upload ${type}`}
                                            type={type}
                                            displayStyle='box'
                                        />

                                        {type === 'Video' && (
                                            <Button
                                                variant='contained'
                                                color='secondary'
                                                style={{ marginTop: '1rem' }}
                                                onClick={() => openVideoUrl()}
                                            >
                                                Link Video (YouTube/Vimeo)
                                            </Button>
                                        )}
                                    </FormControl>
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormGroup>
                                    <FormLabel>Case Study Category</FormLabel>
                                    <FormControl variant='filled' component={Box} width='100%'>
                                        <Select
                                            value={draft.category || ''}
                                            onChange={(e) => updateField('category', e.target.value)}
                                            input={<OutlinedInput />}
                                            style={{ marginBottom: '0.5rem' }}
                                        >
                                            <MenuItem value='Residential'>Residential</MenuItem>
                                            <MenuItem value='Commercial'>Commercial</MenuItem>
                                        </Select>
                                    </FormControl>
                                </FormGroup>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormGroup>
                                    <FormLabel>Case Study County (optional)</FormLabel>
                                    <FormControl variant='filled' component={Box} width='100%'>
                                        <CountySelect
                                            counties={draft.county || ''}
                                            updateCounties={(county) => updateField('county', county)}
                                            allOption={false}
                                            multiple={false}
                                        />
                                    </FormControl>
                                </FormGroup>
                            </Grid>

                            <Grid item xs={12}>
                                <FormGroup>
                                    <FormLabel>Case Study Description</FormLabel>
                                    <FormControl variant='filled' component={Box} width='100%'>
                                        <ReactQuill
                                            theme="snow"
                                            value={draft.description || ''}
                                            onChange={value => updateField('description', value)}
                                            modules={quillModules}
                                            formats={quillFormats}
                                            style={{ background: '#fff' }}
                                        />
                                    </FormControl>
                                </FormGroup>
                            </Grid>
                            {/* Structured fields (optional, all-or-none) */}
                            <Grid item xs={12} style={{ marginBottom: 16 }}>
                                <FormGroup>
                                    
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Box component={Typography} variant='h3'>
                                                <Box component='span'>Additional Details (Recommended)</Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormGroup>
                                                <FormLabel>How many panels?</FormLabel>
                                                <FormControl
                                                    variant='filled'
                                                    component={Box}
                                                    width='100%'
                                                >
                                                    <OutlinedInput
                                                        autoComplete='off'
                                                        type='number'
                                                        placeholder='e.g. 12'
                                                        value={structured.panels}
                                                        onChange={e => setStructured(s => ({ ...s, panels: e.target.value }))}
                                                        inputProps={{ min: 0 }}
                                                    />
                                                </FormControl>
                                            </FormGroup>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormGroup>
                                                <FormLabel>Cost after grant (€)</FormLabel>
                                                <FormControl
                                                    variant='filled'
                                                    component={Box}
                                                    width='100%'
                                                >
                                                    <OutlinedInput
                                                        autoComplete='off'
                                                        type='number'
                                                        placeholder='e.g. 6500'
                                                        value={structured.costAfterGrant}
                                                        onChange={e => setStructured(s => ({ ...s, costAfterGrant: e.target.value }))}
                                                        inputProps={{ min: 0 }}
                                                    />
                                                </FormControl>
                                            </FormGroup>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormGroup>
                                                <FormLabel>Estimated annual electricity savings (€)</FormLabel>
                                                <FormControl
                                                    variant='filled'
                                                    component={Box}
                                                    width='100%'
                                                >
                                                    <OutlinedInput
                                                        autoComplete='off'
                                                        type='number'
                                                        placeholder='e.g. 1200'
                                                        value={structured.annualSavings}
                                                        onChange={e => setStructured(s => ({ ...s, annualSavings: e.target.value }))}
                                                        inputProps={{ min: 0 }}
                                                    />
                                                </FormControl>
                                            </FormGroup>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormGroup>
                                                <FormLabel>Payback period (years)</FormLabel>
                                                <FormControl
                                                    variant='filled'
                                                    component={Box}
                                                    width='100%'
                                                >
                                                    <OutlinedInput
                                                        autoComplete='off'
                                                        type='number'
                                                        placeholder='Auto-calculated'
                                                        value={paybackPeriod}
                                                        readOnly
                                                    />
                                                </FormControl>
                                            </FormGroup>
                                        </Grid>
                                    </Grid>
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display='flex' justifyContent='space-between' width='100%'>
                                    <Button onClick={props.onClose} color="primary" variant="outlined">
                                        Close
                                    </Button>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        onClick={addProject}
                                        disabled={checkFields() || loading}
                                    >
                                        {!loading ? 'Add Case Study' : <Loader height={12} />}
                                    </Button>
                                </Box>
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

export default AddProjectCard